if (typeof window !== 'undefined') {
  /* eslint-disable @typescript-eslint/no-require-imports */
  require('lightgallery.js')
  require('lg-zoom.js')
  require('lg-thumbnail.js')
  require('lg-fullscreen.js')
  /* eslint-enable @typescript-eslint/no-require-imports */
}

type GalleryImage = {
  src: string
  thumb: string
  subHtml: string
}

/**
 * Converts preview images into gallery format with captions.
 *
 * Transforms an array of preview objects into the format required by lightGallery.
 * Generates appropriate captions for single or multi-page documents.
 *
 * @param caption - Base caption text for the images
 * @param previews - Array of preview objects with image and thumb URLs
 * @returns Array of gallery images formatted for lightGallery, or empty array if no previews
 *
 * @example
 * ```typescript
 * const previews = [
 *   { image: 'full1.jpg', thumb: 'thumb1.jpg' },
 *   { image: 'full2.jpg', thumb: 'thumb2.jpg' }
 * ]
 *
 * const galleryImages = getGalleryImages('My Document', previews)
 * // Returns:
 * // [
 * //   { src: 'full1.jpg', thumb: 'thumb1.jpg', subHtml: '<h4>My Document - Page 1</h4>' },
 * //   { src: 'full2.jpg', thumb: 'thumb2.jpg', subHtml: '<h4>My Document - Page 2</h4>' }
 * // ]
 *
 * const singleImage = getGalleryImages('Single Image', [{ image: 'img.jpg', thumb: 'thumb.jpg' }])
 * // Returns: [{ src: 'img.jpg', thumb: 'thumb.jpg', subHtml: '<h4>Single Image</h4>' }]
 * ```
 */
export const getGalleryImages = (
  caption: string,
  previews?:
    | {
        image: string
        thumb: string
      }[]
    | null,
): GalleryImage[] => {
  if (!previews) return []
  return previews.map((p, index) => {
    const title =
      previews.length > 1 ? `${caption} - Page ${index + 1}` : caption
    return {
      src: p.image,
      thumb: p.thumb,
      subHtml: `<h4>${title}</h4>`,
    }
  })
}

/**
 * Initializes and attaches a lightGallery instance to a DOM element.
 *
 * Sets up a dynamic lightGallery with zoom, thumbnail, and fullscreen capabilities.
 * Configures the gallery with fade transitions and enables thumbnails for multi-image galleries.
 *
 * @param elementId - The DOM element ID to attach the gallery to
 * @param galleryImages - Array of gallery images to display
 * @returns void
 *
 * @example
 * ```typescript
 * const images = getGalleryImages('My Gallery', previews)
 * attachGallery('gallery-container', images)
 *
 * // Now clicking on #gallery-container will open the lightGallery
 * ```
 */
export const attachGallery = (
  elementId: string,
  galleryImages: GalleryImage[],
): void =>
  window.lightGallery(document.getElementById(elementId), {
    dynamic: true,
    download: false,
    mode: 'lg-fade',
    preload: 2,
    counter: galleryImages.length > 1,
    dynamicEl: galleryImages,
    thumbnail: true,
    toggleThumb: true,
  })
